import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileSpreadsheet, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { API, fmtDate, downloadExcel } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";

const empty = { name: "", company: "", phone: "", email: "", address: "", gstin: "", source: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const [pg, setPg] = useState({ page: 1, size: 10 });
  const [cities, setCities] = useState([]);

  const load = useCallback(async () => {
    const { data } = await API.get("/customers", { params: { search } });
    setCustomers(data);
  }, [search]);

  useEffect(() => {
    load().catch(() => {});
    API.get("/meta/cities").then((r) => setCities(r.data)).catch(() => {});
  }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (editId) {
      await API.patch(`/customers/${editId}`, form);
      toast.success("Customer updated");
    } else {
      await API.post("/customers", form);
      toast.success("Customer created");
    }
    setOpen(false);
    setForm(empty);
    setEditId(null);
    load();
  };

  const remove = async (id) => {
    await API.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const sorted = [...customers].sort((a, b) => {
    if (!sort.key) return 0;
    const va = sort.key === "since" ? a.created_at || "" : a[sort.key] ?? 0;
    const vb = sort.key === "since" ? b.created_at || "" : b[sort.key] ?? 0;
    if (typeof va === "string") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sort.dir === "asc" ? va - vb : vb - va;
  });
  const paged = sorted.slice((pg.page - 1) * pg.size, pg.page * pg.size);

  const SortHead = ({ label, k, className = "" }) => (
    <TableHead className={`cursor-pointer select-none ${className}`} onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      {label} <ArrowUpDown className="inline h-3 w-3 text-slate-400" />
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500">Won leads and direct customers</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPg((p) => ({ ...p, page: 1 })); }} className="w-64" data-testid="customer-search-input" />
          <Button variant="outline" onClick={() => downloadExcel("/customers/export", "customers.xlsx")} data-testid="customers-export-button">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="new-customer-button">
            <Plus className="mr-1 h-4 w-4" /> New Customer
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead label="Name" k="name" />
              <SortHead label="Company" k="company" />
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Source</TableHead>
              <SortHead label="Total Leads" k="total_leads" className="text-center" />
              <SortHead label="Won Leads" k="won_leads" className="text-center" />
              <SortHead label="Since" k="since" />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="customers-table">
            {paged.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.company || "-"}</TableCell>
                <TableCell>{c.phone || "-"}</TableCell>
                <TableCell>{c.email || "-"}</TableCell>
                <TableCell>{c.source ? <Badge variant="outline">{c.source}</Badge> : "-"}</TableCell>
                <TableCell className="text-center" data-testid={`customer-total-leads-${c.id}`}>
                  <Badge variant="secondary">{c.total_leads ?? 0}</Badge>
                </TableCell>
                <TableCell className="text-center" data-testid={`customer-won-leads-${c.id}`}>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{c.won_leads ?? 0}</Badge>
                </TableCell>
                <TableCell>{fmtDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); }} data-testid={`edit-customer-${c.id}`} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={9}><EmptyState compact title="No customers yet" hint="Convert a won lead or add one manually." /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination total={sorted.length} page={pg.page} size={pg.size} onChange={setPg} testid="customers-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Customer" : "New Customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5" data-testid="customer-company-input" />
            </div>
            <div>
              <Label>Phone (WhatsApp)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="customer-phone-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="customer-email-input" />
            </div>
            <div>
              <Label>City</Label>
              <Input list="customer-city-list" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Type to search cities..." className="mt-1.5" data-testid="customer-city-input" autoComplete="off" />
              <datalist id="customer-city-list">
                {cities.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1.5" data-testid="customer-gstin-input" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="customer-address-input" />
            </div>
          </div>
          <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="save-customer-button">
            {editId ? "Save Changes" : "Create Customer"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
